/**
 * Clip import.
 *
 * The method needs a few seconds of a moving phone, and a video already on the
 * camera roll is exactly that. Frames are sampled evenly across the clip rather
 * than taken consecutively, because the pipeline wants the phone in as many
 * different places as possible and a burst of neighbouring frames gives it one.
 */
import { mk, drawCtx } from '../vision/image';

export interface ImportOptions {
  /** How many frames to pull out. */
  frames: number;
  /** Longest edge of each extracted frame. */
  longEdge: number;
  /** Seconds of the clip to sample from, measured from the start. */
  maxSeconds?: number;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export interface ImportedClip {
  frames: HTMLCanvasElement[];
  w: number;
  h: number;
}

/** Seek and wait for the frame to actually be painted. */
function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      video.removeEventListener('seeked', done);
      resolve();
    };
    video.addEventListener('seeked', done);
    video.currentTime = time;
    // Some containers never fire `seeked` for a time they cannot land on
    // exactly. Waiting forever is worse than sampling a neighbouring frame.
    setTimeout(done, 900);
  });
}

export async function importClip(file: File, opts: ImportOptions): Promise<ImportedClip> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.playsInline = true;
  video.muted = true;
  video.preload = 'auto';
  video.src = url;

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('That file could not be read as a video.'));
      setTimeout(() => reject(new Error('That video took too long to open.')), 15000);
    });

    const sourceW = video.videoWidth;
    const sourceH = video.videoHeight;
    if (!sourceW || !sourceH) throw new Error('That video has no picture track.');

    const long = opts.longEdge;
    const [w, h] =
      sourceH >= sourceW
        ? [Math.round((sourceW * long) / sourceH), long]
        : [long, Math.round((sourceH * long) / sourceW)];

    const duration = Math.min(video.duration || 3, opts.maxSeconds ?? 10);
    const frames: HTMLCanvasElement[] = [];

    for (let i = 0; i < opts.frames; i++) {
      if (opts.signal?.aborted) throw new DOMException('Import cancelled', 'AbortError');
      await seekTo(video, (duration * (i + 0.5)) / opts.frames);
      const canvas = mk(w, h);
      drawCtx(canvas).drawImage(video, 0, 0, w, h);
      frames.push(canvas);
      opts.onProgress?.((i + 1) / opts.frames);
    }

    if (frames.length < 4) throw new Error('That clip is too short to work from.');
    return { frames, w, h };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}
